import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BASE_API_URL } from "../../utils/consts";
import { useSelector } from "react-redux";
import { useAuth } from "../../utils/useAuth";
import { isSuperAdmin } from "../../utils/permissions";
import { usePermissions } from "../../utils/usePermissions";
import AppModal from "../../Components/Modals/AppModal/AppModal";
import "./SupplierProfile.scss";

const AVATAR_GRADIENTS = [
  "linear-gradient(135deg, #f97316, #ec4899)",
  "linear-gradient(135deg, #3b82f6, #8b5cf6)",
  "linear-gradient(135deg, #10b981, #3b82f6)",
  "linear-gradient(135deg, #f59e0b, #ef4444)",
  "linear-gradient(135deg, #8b5cf6, #ec4899)",
  "linear-gradient(135deg, #06b6d4, #3b82f6)",
];

const getAvatarGradient = (seed: string) => {
  if (!seed) return AVATAR_GRADIENTS[0];
  return AVATAR_GRADIENTS[seed.charCodeAt(seed.length - 1) % AVATAR_GRADIENTS.length];
};

const getInitials = (naziv = "") =>
  naziv.split(" ").slice(0, 2).map((w) => w[0] ?? "").join("").toUpperCase();

const TruckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 7h11v10H3zM14 10h4l3 3v4h-7" />
    <circle cx="7" cy="18" r="1.6" />
    <circle cx="17" cy="18" r="1.6" />
  </svg>
);

const PERSON_GRADIENTS = [
  "linear-gradient(135deg,#f97316,#ec4899)",
  "linear-gradient(135deg,#3b82f6,#8b5cf6)",
  "linear-gradient(135deg,#10b981,#3b82f6)",
  "linear-gradient(135deg,#f59e0b,#ef4444)",
  "linear-gradient(135deg,#8b5cf6,#ec4899)",
  "linear-gradient(135deg,#06b6d4,#3b82f6)",
];
const personGradient = (id: string) =>
  PERSON_GRADIENTS[(id?.charCodeAt(id.length - 1) ?? 0) % PERSON_GRADIENTS.length];
const personInitials = (u: any) =>
  `${u?.firstName?.[0] ?? ""}${u?.lastName?.[0] ?? ""}`.toUpperCase();
const normalizeZaduzeni = (sup: any): any[] => {
  if (!sup?.zaduzeni) return [];
  return Array.isArray(sup.zaduzeni) ? sup.zaduzeni : [sup.zaduzeni];
};

const SupplierProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [supplier, setSupplier] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const [nazivError, setNazivError] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [showUserDrop, setShowUserDrop] = useState(false);
  const navigate = useNavigate();
  const userData = useSelector((state: any) => state.userData.user);
  const authUser: any = useAuth();
  const { hasPermission } = usePermissions();
  const isSuper = isSuperAdmin(userData?.roles || authUser?.roles);
  const canEdit = hasPermission("suppliers:update") || isSuper;

  const clientId =
    typeof userData?.clientId === "object" ? userData?.clientId?._id : userData?.clientId;

  useEffect(() => {
    if (clientId && id) {
      axios
        .get(`${BASE_API_URL}/${clientId}/suppliers/${id}`)
        .then((res) => setSupplier(res.data))
        .catch(() => {});
      axios
        .get(`${BASE_API_URL}/${clientId}/users`)
        .then((res) => setUsers(res.data))
        .catch(() => {});
    }
  }, [clientId, id]);

  const handleEditOpen = () => {
    setFormData({
      naziv: supplier.naziv ?? "",
      kontaktOsoba: supplier.kontaktOsoba ?? "",
      email: supplier.email ?? "",
      telefon: supplier.telefon ?? "",
      komercijalist: supplier.komercijalist ?? "",
      zaduzeniIds: normalizeZaduzeni(supplier).map((z: any) => z._id?.toString()).filter(Boolean),
      datumNarudzbe: supplier.datumNarudzbe ?? "",
      napomena: supplier.napomena ?? "",
    });
    setNazivError(false);
    setUserSearch("");
    setIsEditOpen(true);
  };

  const setField = (key: string) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData((prev: any) => ({ ...prev, [key]: e.target.value }));
    if (key === "naziv") setNazivError(false);
  };

  const filteredUserOptions = users.filter((u: any) => {
    const ids: string[] = formData.zaduzeniIds || [];
    if (ids.includes(u._id)) return false;
    const q = userSearch.toLowerCase();
    return !q || `${u.firstName} ${u.lastName}`.toLowerCase().includes(q);
  });

  const addZaduzeni = (uid: string) => {
    setFormData((prev: any) => ({ ...prev, zaduzeniIds: [...(prev.zaduzeniIds || []), uid] }));
    setUserSearch("");
    setShowUserDrop(false);
  };

  const removeZaduzeni = (uid: string) => {
    setFormData((prev: any) => ({
      ...prev,
      zaduzeniIds: (prev.zaduzeniIds || []).filter((i: string) => i !== uid),
    }));
  };

  const handleSave = async () => {
    if (!formData.naziv?.trim()) {
      setNazivError(true);
      return;
    }
    const zaduzeni = (formData.zaduzeniIds || [])
      .map((uid: string) => {
        const u = users.find((u: any) => u._id === uid);
        return u ? { _id: u._id, firstName: u.firstName, lastName: u.lastName } : null;
      })
      .filter(Boolean);
    try {
      const res = await axios.put(`${BASE_API_URL}/${clientId}/suppliers/${id}`, { ...formData, zaduzeni });
      setSupplier(res.data);
      setIsEditOpen(false);
    } catch (_) {}
  };

  if (!supplier) {
    return (
      <div className="mainContainer supplier-profile-page">
        <div className="sp-loading">Učitavanje...</div>
      </div>
    );
  }

  return (
    <div className="mainContainer supplier-profile-page">
      {/* Back */}
      <button className="sp-back" onClick={() => navigate("/suppliers")}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M15 18l-6-6 6-6" />
        </svg>
        Dobavljači
      </button>

      {/* Header card */}
      <div className="sp-header-card">
        <div className="sp-avatar-lg" style={{ background: getAvatarGradient(supplier._id || supplier.naziv || "") }}>
          {getInitials(supplier.naziv)}
        </div>
        <div className="sp-header-info">
          <h1 className="sp-company-name">{supplier.naziv}</h1>
          {supplier.kontaktOsoba && <div className="sp-company-sub">{supplier.kontaktOsoba}</div>}
        </div>
        <div className="sp-spacer" />
        {canEdit && (
          <button className="sp-edit-btn" onClick={handleEditOpen}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            Uredi
          </button>
        )}
      </div>

      {/* Info grid */}
      <div className="sp-grid">
        <div className="sp-card">
          <div className="sp-card-icon">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
              <rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" />
            </svg>
          </div>
          <div className="sp-card-label">Email</div>
          <div className="sp-card-value">
            {supplier.email ? (
              <a href={`mailto:${supplier.email}`} className="sp-link">{supplier.email}</a>
            ) : <span className="sp-empty">—</span>}
          </div>
        </div>

        <div className="sp-card">
          <div className="sp-card-icon">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.57 3.5 2 2 0 0 1 3.55 1.32h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 9a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
          </div>
          <div className="sp-card-label">Telefon</div>
          <div className="sp-card-value sp-mono">{supplier.telefon || <span className="sp-empty">—</span>}</div>
        </div>

        <div className="sp-card">
          <div className="sp-card-icon">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
            </svg>
          </div>
          <div className="sp-card-label">Komercijalist</div>
          <div className="sp-card-value">{supplier.komercijalist || <span className="sp-empty">—</span>}</div>
        </div>

        <div className="sp-card">
          <div className="sp-card-icon">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <div className="sp-card-label">Ko vodi dobavljača</div>
          <div className="sp-card-value">
            {normalizeZaduzeni(supplier).length > 0 ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                {normalizeZaduzeni(supplier).map((z: any) => (
                  <span key={z._id?.toString()} className="am-mu-chip">
                    <span className="am-mu-avatar" style={{ background: personGradient(z._id?.toString() ?? "") }}>
                      {personInitials(z)}
                    </span>
                    {z.firstName} {z.lastName}
                  </span>
                ))}
              </div>
            ) : <span className="sp-empty">—</span>}
          </div>
        </div>

        <div className="sp-card">
          <div className="sp-card-icon">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          </div>
          <div className="sp-card-label">Datum narudžbe</div>
          <div className="sp-card-value">{supplier.datumNarudzbe || <span className="sp-empty">—</span>}</div>
        </div>

        {supplier.napomena && (
          <div className="sp-card sp-card--full">
            <div className="sp-card-icon">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
              </svg>
            </div>
            <div className="sp-card-label">Napomena</div>
            <div className="sp-card-value">{supplier.napomena}</div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <AppModal
        open={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        icon={<TruckIcon />}
        eyebrow="Uredi dobavljača"
        title="Uredi podatke"
        description="Izmijenite informacije o ovom dobavljaču."
        onSubmit={handleSave}
        submitLabel="Sačuvaj izmjene"
      >
        <div className="am-section">
          <div className="am-section-label">Podaci dobavljača</div>
          <div className="am-form-grid">

            <div className="am-field am-span-2">
              <label className="am-label">Naziv firme <span className="am-req">*</span></label>
              <input
                className={`am-input${nazivError ? " am-input-error" : ""}`}
                value={formData.naziv}
                onChange={setField("naziv")}
              />
              {nazivError && <span className="am-error-msg">Naziv je obavezan</span>}
            </div>

            <div className="am-field am-span-2">
              <label className="am-label">Kontakt osoba / broj</label>
              <input className="am-input" value={formData.kontaktOsoba} onChange={setField("kontaktOsoba")} />
            </div>

            <div className="am-field">
              <label className="am-label">Email</label>
              <div className="am-input-affix">
                <span className="am-ic-lead">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3.5 6.5L12 13l8.5-6.5" />
                  </svg>
                </span>
                <input className="am-input am-input-lead" type="email" value={formData.email} onChange={setField("email")} />
              </div>
            </div>

            <div className="am-field">
              <label className="am-label">Telefon</label>
              <div className="am-input-affix">
                <span className="am-ic-lead">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 3h3l1.5 5L8 9.5a12 12 0 0 0 6.5 6.5L16 14l5 1.5V19a2 2 0 0 1-2.2 2A16 16 0 0 1 3 5.2 2 2 0 0 1 5 3z" />
                  </svg>
                </span>
                <input className="am-input am-input-lead" type="tel" value={formData.telefon} onChange={setField("telefon")} />
              </div>
            </div>

            <div className="am-field am-span-2">
              <label className="am-label">
                Ime komercijaliste
                <span className="am-field-hint">sa strane dobavljača</span>
              </label>
              <input className="am-input" value={formData.komercijalist} onChange={setField("komercijalist")} />
            </div>
          </div>
        </div>

        <div className="am-section">
          <div className="am-section-label">Interno upravljanje</div>
          <div className="am-form-grid">

            <div className="am-field am-span-2">
              <label className="am-label">Ko vodi ovog dobavljača</label>
              <div className="am-mu-picker">
                {(formData.zaduzeniIds || []).length > 0 && (
                  <div className="am-mu-chips">
                    {(formData.zaduzeniIds || []).map((uid: string) => {
                      const u = users.find((u: any) => u._id === uid);
                      if (!u) return null;
                      return (
                        <span key={uid} className="am-mu-chip">
                          <span className="am-mu-avatar" style={{ background: personGradient(uid) }}>
                            {personInitials(u)}
                          </span>
                          {u.firstName} {u.lastName}
                          <button type="button" onClick={() => removeZaduzeni(uid)}>×</button>
                        </span>
                      );
                    })}
                  </div>
                )}
                <div className="am-mu-input-wrap">
                  <input
                    className="am-input"
                    placeholder="Traži zaposlenog…"
                    value={userSearch}
                    onFocus={() => setShowUserDrop(true)}
                    onBlur={() => setTimeout(() => setShowUserDrop(false), 150)}
                    onChange={(e) => { setUserSearch(e.target.value); setShowUserDrop(true); }}
                  />
                  {showUserDrop && filteredUserOptions.length > 0 && (
                    <div className="am-mu-dropdown">
                      {filteredUserOptions.map((u: any) => (
                        <button
                          key={u._id}
                          type="button"
                          className="am-mu-option"
                          onMouseDown={() => addZaduzeni(u._id)}
                        >
                          <span className="am-mu-avatar" style={{ background: personGradient(u._id) }}>
                            {personInitials(u)}
                          </span>
                          {u.firstName} {u.lastName}
                        </button>
                      ))}
                    </div>
                  )}
                  {showUserDrop && filteredUserOptions.length === 0 && userSearch && (
                    <div className="am-mu-dropdown">
                      <div className="am-mu-empty">Nema rezultata</div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="am-field am-span-2">
              <label className="am-label">Kada se poručuje</label>
              <input className="am-input" value={formData.datumNarudzbe} onChange={setField("datumNarudzbe")} />
            </div>

            <div className="am-field am-span-2">
              <label className="am-label">Napomena</label>
              <textarea className="am-textarea" value={formData.napomena} onChange={setField("napomena")} />
            </div>
          </div>
        </div>
      </AppModal>
    </div>
  );
};

export default SupplierProfile;
